import { get } from 'svelte/store'
import { accessTokenStore, fn } from '@packages/stores'
import { ReceiptTarget } from '@packages/utils/enums'
import { configStore } from '@src/stores/config'
import { getClientTokenConfig } from '@lib/clients'
import type { TLicensePaymentData, TMembershipPaymentData, TTokenPaymentData } from './oders.types'
import { dispatch, PassportEvents } from '@lib/events/events'
import { TransactionTypes } from '@packages/utils/enums'
import { pageOptsStore, pageStore, userStore } from '@packages/stores'
import { Pages } from '@packages/utils/enums'
import { analytics } from '@lib/mixpanel/mixpanel'
import { waitVideoEnded } from '@components/progress/ProgressVideo.service'
import { getAddress } from '@src/passport/blockchain'
import { validateOrderItems, validateTxs } from '@lib/orders'
import type { TPaymentReturn } from '@packages/utils/types'

export const requestReceipt = async ({
  hash,
  paymentIntentId,
  title,
  subtitle,
  amount,
  currency,
}: {
  hash?: string
  paymentIntentId?: string
  title?: string
  subtitle?: string
  amount?: number
  currency?: string
}) => {
  const { credenza, receiptTarget } = get(configStore)

  const targets = !receiptTarget
    ? [ReceiptTarget.EMAIL]
    : receiptTarget.filter((target) => {
        const user = get(userStore)
        if (!user) return
        return user[target]
      })
  let successCount = 0

  for (const target of targets) {
    const result = await fetch(`${credenza.evmApiUrl}/contracts/receipt`, {
      method: 'POST',
      body: JSON.stringify({
        ...(hash ? { tx: hash } : { pi_id: paymentIntentId }),
        ...(amount ? { amount } : {}),
        ...(currency ? { currency } : {}),
        target,
        title,
        subtitle,
        source: getClientTokenConfig().name,
      }),
      headers: {
        Authorization: `Bearer ${get(accessTokenStore)}`,
        'Content-Type': 'application/json',
      },
    })

    if (result.ok) {
      successCount++
    } else {
      console.warn(`Failed to send receipt to ${target}:`, result.status)
    }
  }

  return successCount > 0
}

export const afterPaymentProcess = async (
  res: TPaymentReturn,
  priceCents: number,
  purchaseConfig: {
    title: string
    subtitle: string
    tokens: TTokenPaymentData[]
    memberships: TMembershipPaymentData[]
    licenses: TLicensePaymentData[]
  },
) => {
  const { toastAlert } = get(fn)
  const config = get(configStore)

  const failedOrdersMessage = validateOrderItems(res.items)
  if (!!purchaseConfig.tokens?.length || !!purchaseConfig.memberships?.length)
    validateTxs(res.chain_txs, failedOrdersMessage)

  dispatch(PassportEvents.PAYMENT, {
    type: TransactionTypes.CARD,
    results: {
      ...res,
      tokens: purchaseConfig.tokens,
      memberships: purchaseConfig.memberships,
      licenses: purchaseConfig.licenses,
    },
  })
  toastAlert('Your payment was successful')
  dispatch(PassportEvents.RECHECK_BALANCE, res.chain_txs)

  const user = get(userStore)
  if (user) {
    analytics.track('cp_payment_fiat', {
      $email: user.email,
      $phone: user.phone,
      chain: config.chainId,
      clientId: config.clientId,
      address: await getAddress(),
      location: window.location.href,
      tokens: purchaseConfig?.tokens,
      memberships: purchaseConfig?.memberships,
      licenses: purchaseConfig?.licenses,
      total: priceCents / 100,
      description: (purchaseConfig?.title ?? '') + `(${purchaseConfig?.subtitle ?? ''})`,
    })
  }

  await waitVideoEnded()

  if (priceCents === 0) pageOptsStore.set({ subtitle: 'Your claim was successful!', receipt: false })

  if (failedOrdersMessage) {
    pageOptsStore.set({
      title: 'Done!',
      subtitle: 'Your payment was partly successful',
      orders: res.items,
    })
  }
  pageStore.set(Pages.PAYMENT_RESULT)
}
